import React, { useState, useEffect } from 'react';
import { studioApiService } from '../../services/studioApiService';

interface S3DirectRow {
  id: string;
  row_id: string;
  product_id?: string;
  product_link?: string;
  name: string;
  brand: string;
  marketplace: string;
  color: string;
  price: string;
  discount_price: string;
  available_sizes: string;
  description: string;
  gender: string;
  related_products: string; // Comma-separated product IDs
  primaryImage?: string;
  secondaryImages?: string[];
  s3Status: 'idle' | 'fetching' | 'found' | 'not_found' | 'error';
  dbStatus: 'pending' | 'submitting' | 'submitted' | 'error';
  error?: string;
}

export const DirectS3DbUpload: React.FC = () => {
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncStatusMsg, setSyncStatusMsg] = useState<string | null>(null);
  const createEmptyRow = (): S3DirectRow => ({
    id: crypto.randomUUID(),
    row_id: '',
    name: '',
    brand: 'Slayd Originals',
    marketplace: 'Slayd',
    color: '',
    price: '',
    discount_price: '',
    available_sizes: 'XS, S, M, L, XL',
    description: '',
    gender: 'Women',
    related_products: '',
    s3Status: 'idle',
    dbStatus: 'pending'
  });

  const [rows, setRows] = useState<S3DirectRow[]>([createEmptyRow()]);

  // Always clear cached rows on fresh page land / reload
  useEffect(() => {
    try {
      localStorage.removeItem('slayd_direct_s3_rows');
    } catch (e) {}
  }, []);

  const handleAddRow = () => {
    setRows(prev => [
      ...prev,
      createEmptyRow()
    ]);
  };

  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear all product rows?")) {
      setRows([createEmptyRow()]);
      try {
        localStorage.removeItem('slayd_direct_s3_rows');
      } catch (e) {}
    }
  };

  const handleRemoveRow = (id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const updateRow = (id: string, updates: Partial<S3DirectRow>) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  };

  const handleFetchS3 = async (rowId: string, id: string) => {
    const cleanRowId = rowId.trim();
    if (!cleanRowId) return;

    updateRow(id, { s3Status: 'fetching', error: undefined });

    try {
      const res = await studioApiService.fetchS3ImagesByRowId(cleanRowId);
      if (res.success && res.primaryImage) {
        updateRow(id, {
          s3Status: 'found',
          primaryImage: res.primaryImage,
          secondaryImages: res.secondaryImages || []
        });
      } else {
        updateRow(id, { s3Status: 'not_found', error: `No S3 images found for ${cleanRowId}` });
      }
    } catch (e: any) {
      updateRow(id, { s3Status: 'error', error: e.message || `No S3 images found for ${cleanRowId}` });
    }
  };

  // Dynamic debounced S3 lookup as user types or pastes Row ID
  const handleRowIdChange = (id: string, value: string) => {
    updateRow(id, { row_id: value });
  };

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    rows.forEach(row => {
      const clean = row.row_id.trim();
      if (clean && row.s3Status !== 'fetching' && row.s3Status !== 'found') {
        const t = setTimeout(() => {
          handleFetchS3(clean, row.id);
        }, 400);
        timers.push(t);
      }
    });

    return () => {
      timers.forEach(t => clearTimeout(t));
    };
  }, [rows.map(r => r.row_id).join(',')]);

  // ── Google Sheets Sync Handler ──
  const handleSyncGoogleSheet = async () => {
    setIsSyncing(true);
    setSyncStatusMsg('Fetching available products from Google Sheet...');
    try {
      const data = await studioApiService.syncGoogleSheet();
      if (data.rows && data.rows.length > 0) {
        setRows(data.rows);
        setSyncStatusMsg(`✓ Synced ${data.rows.length} available products!`);
        setTimeout(() => setSyncStatusMsg(null), 4000);
      } else {
        setSyncStatusMsg('No available products found (is_available = TRUE)');
        setTimeout(() => setSyncStatusMsg(null), 4000);
      }
    } catch (err: any) {
      setSyncStatusMsg(`Sync error: ${err.message}`);
      alert(`Google Sheet Sync Error: ${err.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSubmitRow = async (row: S3DirectRow) => {
    if (!row.name.trim()) {
      alert("Please enter a Product Name.");
      return;
    }
    if (!row.row_id.trim()) {
      alert("Please enter a Row ID.");
      return;
    }

    const imgUrl = (row.primaryImage || '').trim().replace(/\s+/g, '');
    if (!imgUrl || !imgUrl.startsWith('http')) {
      updateRow(row.id, { dbStatus: 'error', error: 'No valid S3 image URL. Please fetch S3 images first.' });
      return;
    }

    const parsedPrice = parseFloat(row.price) || 0;
    const parsedDiscountPrice = parseFloat(row.discount_price) || parsedPrice;

    // Clean sizes
    const sizesList = row.available_sizes
      .split(',')
      .map(s => s.trim().toUpperCase())
      .filter(Boolean);
    const formattedSizes = sizesList.join(', ');

    // Clean related products
    const relatedList = row.related_products
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const prodLink = (row.product_link && row.product_link.trim().startsWith('http'))
      ? row.product_link.trim()
      : `https://slayd.in/internal/feed/${row.row_id.trim()}`;
    const dPrice = parseFloat(parsedDiscountPrice.toFixed(2));

    const payload = {
      // CamelCase keys (Backend DRF requirement)
      imageUrl: imgUrl,
      secondaryImages: row.secondaryImages || [],
      productLink: prodLink,
      discountPrice: dPrice,
      relatedProducts: relatedList,
      availableSizes: formattedSizes,
      isSlaydOriginals: true,
      isAvailable: true,

      // Snake_case keys (Compatibility)
      image_url: imgUrl,
      secondary_images: (row.secondaryImages || []).map((u: string) => u.trim().replace(/\s+/g, '')),
      product_link: prodLink,
      discount_price: dPrice,
      related_products: relatedList,
      available_sizes: formattedSizes,
      is_slayd_originals: true,
      is_available: true,

      // Common fields
      product_id: row.row_id.trim(),
      productId: row.row_id.trim(),
      title: row.name.trim(),
      name: row.name.trim(),
      brand: row.brand.trim() || 'Slayd Originals',
      marketplace: 'Slayd',
      price: parsedPrice,
      mrp: dPrice,
      color: row.color.trim(),
      gender: 'Women',
      description: row.description.trim(),
      slayd_originals_color: row.color.trim(),
      slayd_originals_size: formattedSizes,
      slayd_originals_description: row.description.trim()
    };

    updateRow(row.id, { dbStatus: 'submitting', error: undefined });

    try {
      const res = await studioApiService.createProductInDB(payload);
      const assignedId = res?.id || res?.product?.id || res?.product_id || row.product_id;
      const finalIdStr = assignedId ? String(assignedId) : row.product_id;
      updateRow(row.id, { 
        dbStatus: 'submitted',
        product_id: finalIdStr
      });

      if (finalIdStr && row.row_id.trim()) {
        studioApiService.updateSheetProductIds([{ row_id: row.row_id.trim(), product_id: finalIdStr }])
          .then(() => setSyncStatusMsg(`✓ Updated product ID in Google Sheets!`))
          .catch(console.error);
      }
    } catch (e: any) {
      updateRow(row.id, { dbStatus: 'error', error: e.message });
    }
  };

  const handleSubmitAll = async () => {
    const readyRows = rows.filter(r => r.primaryImage && r.name.trim() && r.dbStatus !== 'submitted');
    if (readyRows.length === 0) {
      return;
    }

    setSyncStatusMsg(`Resetting previous Slayd Originals to unavailable in DB...`);
    try {
      await studioApiService.markUnavailableByBrand('Slayd Originals', 'Slayd');
      await studioApiService.markUnavailableByBrand('Slayd Originals', '');
    } catch (e: any) {
      console.warn('[Studio] Failed to reset brand availability:', e.message);
    }

    setSyncStatusMsg(`Submitting ${readyRows.length} active products to DB...`);
    const sheetUpdates: { row_id: string; product_id: string }[] = [];

    for (const r of readyRows) {
      try {
        const parsedPrice = parseFloat(r.price) || 0;
        const parsedDiscountPrice = parseFloat(r.discount_price) || parsedPrice;
        const imgUrl = (r.primaryImage || '').trim().replace(/\s+/g, '');
        const sizesList = r.available_sizes.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
        const formattedSizes = sizesList.join(', ');
        const relatedList = r.related_products.split(',').map(s => s.trim()).filter(Boolean);
        const prodLink = (r.product_link && r.product_link.trim().startsWith('http'))
          ? r.product_link.trim()
          : `https://slayd.in/internal/feed/${r.row_id.trim()}`;
        const dPrice = parseFloat(parsedDiscountPrice.toFixed(2));

        const payload = {
          imageUrl: imgUrl,
          secondaryImages: r.secondaryImages || [],
          productLink: prodLink,
          discountPrice: dPrice,
          relatedProducts: relatedList,
          availableSizes: formattedSizes,
          isSlaydOriginals: true,
          isAvailable: true,
          image_url: imgUrl,
          secondary_images: (r.secondaryImages || []).map((u: string) => u.trim().replace(/\s+/g, '')),
          product_link: prodLink,
          discount_price: dPrice,
          related_products: relatedList,
          available_sizes: formattedSizes,
          is_slayd_originals: true,
          is_available: true,
          product_id: r.row_id.trim(),
          productId: r.row_id.trim(),
          title: r.name.trim(),
          name: r.name.trim(),
          brand: r.brand.trim() || 'Slayd Originals',
          marketplace: 'Slayd',
          price: parsedPrice,
          mrp: dPrice,
          color: r.color.trim(),
          gender: 'Women',
          description: r.description.trim(),
          slayd_originals_color: r.color.trim(),
          slayd_originals_size: formattedSizes,
          slayd_originals_description: r.description.trim()
        };

        updateRow(r.id, { dbStatus: 'submitting', error: undefined });
        const res = await studioApiService.createProductInDB(payload);
        const assignedId = res?.id || res?.product?.id || res?.product_id || r.product_id;
        const finalIdStr = assignedId ? String(assignedId) : r.product_id;

        updateRow(r.id, { 
          dbStatus: 'submitted',
          product_id: finalIdStr
        });

        if (finalIdStr && r.row_id.trim()) {
          sheetUpdates.push({ row_id: r.row_id.trim(), product_id: finalIdStr });
        }
      } catch (e: any) {
        updateRow(r.id, { dbStatus: 'error', error: e.message });
      }
    }

    if (sheetUpdates.length > 0) {
      setSyncStatusMsg(`Updating Google Sheets with ${sheetUpdates.length} product IDs...`);
      try {
        await studioApiService.updateSheetProductIds(sheetUpdates);
      } catch (err: any) {
        console.warn('Sheet update error:', err.message);
      }

      // ── Auto-Link Related Products in Django DB ──
      setSyncStatusMsg(`Linking related product groups in DB...`);
      const rowIdToDbIdMap: Record<string, string> = {};
      sheetUpdates.forEach(item => {
        rowIdToDbIdMap[item.row_id.trim()] = item.product_id.trim();
      });

      const linkedGroupKeys = new Set<string>();
      for (const r of readyRows) {
        const myDbId = rowIdToDbIdMap[r.row_id.trim()] || r.product_id;
        if (!myDbId) continue;

        const relRowIds = (r.related_products || '').split(',').map((s: string) => s.trim()).filter(Boolean);
        if (relRowIds.length > 0) {
          const groupDbIds: number[] = [parseInt(myDbId, 10)];
          relRowIds.forEach((relRowId: string) => {
            const relDbId = rowIdToDbIdMap[relRowId];
            if (relDbId) groupDbIds.push(parseInt(relDbId, 10));
          });

          if (groupDbIds.length > 1) {
            const groupKey = [...groupDbIds].sort((a, b) => a - b).join('-');
            if (!linkedGroupKeys.has(groupKey)) {
              linkedGroupKeys.add(groupKey);
              try {
                await studioApiService.linkRelatedProducts(groupDbIds);
                console.log('[Studio] Successfully linked related products group:', groupDbIds);
              } catch (e: any) {
                console.warn('[Studio] Failed to link related products group:', groupDbIds, e.message);
              }
            }
          }
        }
      }

      setSyncStatusMsg(`✓ Submitted all products, updated sheets, and linked related variants!`);
      setTimeout(() => setSyncStatusMsg(null), 5000);
    }
  };

  return (
    <div id="directS3DbPage" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '20px', height: '100%' }}>
      {/* Header Controls (Fixed) */}
      <div style={{ flexShrink: 0 }}>
        <div className="section-label" style={{ fontSize: '16px', marginBottom: '8px', color: 'var(--text)' }}>
          Direct S3 DB Upload
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: '16px' }}>
          Sync available products from Google Sheet or add manually to fetch images from S3 (<code>slayd-originals/products-v2</code>) and submit to Django backend.
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="add-product-btn" style={{ width: 'auto', padding: '8px 16px' }} onClick={handleAddRow}>
            + Add Product Row
          </button>
          
          {/* Sync from Google Sheet Button */}
          <button 
            className="row-dl-btn" 
            style={{ 
              width: 'auto', 
              padding: '8px 16px', 
              background: 'var(--surface2)', 
              border: '1px solid var(--border2)', 
              borderRadius: '8px', 
              fontSize: '12px', 
              color: 'var(--text)', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              cursor: isSyncing ? 'not-allowed' : 'pointer',
              opacity: isSyncing ? 0.7 : 1
            }}
            onClick={handleSyncGoogleSheet}
            disabled={isSyncing}
          >
            {isSyncing ? '⏳ Syncing...' : '🔄 Sync from Google Sheet'}
          </button>

          {syncStatusMsg && (
            <span style={{ fontSize: '12px', color: syncStatusMsg.startsWith('✓') ? '#4caf50' : 'var(--text2)', fontWeight: 500 }}>
              {syncStatusMsg}
            </span>
          )}

          {/* Clear All Rows Button */}
          <button 
            className="row-dl-btn" 
            style={{ width: 'auto', padding: '8px 16px', background: 'var(--surface2)', border: '1px solid var(--border2)', color: '#f44336', borderRadius: '8px', fontSize: '12px' }}
            onClick={handleClearAll}
          >
            🗑️ Clear All Rows
          </button>

          <button 
            className="run-btn primary" 
            style={{ width: 'auto', padding: '8px 20px', marginLeft: 'auto' }}
            onClick={handleSubmitAll}
          >
            Submit All Rows to DB
          </button>
        </div>
      </div>

      {/* Scrollable List Container */}
      <div className="panel-scroll" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', paddingRight: '10px', overflowY: 'auto', minHeight: 0 }}>
        {rows.map((row, i) => {
          const relatedArray = row.related_products.split(',').map(s => s.trim()).filter(Boolean);

          return (
            <div key={row.id} className="product-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', margin: 0, flexShrink: 0, minHeight: 'fit-content' }}>
              <div className="product-card-header" style={{ marginBottom: '14px' }}>
                <div className="product-card-title">
                  Product Entry #{i + 1} {row.row_id ? `(Row ID: ${row.row_id})` : ''} {row.product_id ? `• DB ID: ${row.product_id}` : ''}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span className={`status-badge ${row.dbStatus}`}>{row.dbStatus}</span>
                  {rows.length > 1 && (
                    <button className="product-card-remove" onClick={() => handleRemoveRow(row.id)}>×</button>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px' }}>
                {/* Left Column: Dynamic S3 Image Lookup & Preview */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--surface2)', padding: '12px', borderRadius: '8px' }}>
                  <div className="section-label">1. Dynamic S3 Lookup</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input 
                      type="text" 
                      className="pid-input" 
                      placeholder="Enter Row ID (e.g. 4037)"
                      value={row.row_id}
                      onChange={e => handleRowIdChange(row.id, e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button 
                      className="row-dl-btn" 
                      onClick={() => handleFetchS3(row.row_id, row.id)}
                      disabled={row.s3Status === 'fetching' || !row.row_id.trim()}
                    >
                      {row.s3Status === 'fetching' ? 'Fetching...' : 'Fetch S3'}
                    </button>
                  </div>

                  {/* S3 Status Messages */}
                  {row.s3Status === 'found' && (
                    <div style={{ fontSize: '11px', color: '#4caf50' }}>
                      ✓ S3 Images Found ({1 + (row.secondaryImages?.length || 0)} total)
                    </div>
                  )}
                  {row.s3Status === 'not_found' && (
                    <div style={{ fontSize: '11px', color: '#f44336' }}>
                      ✕ No S3 images found for {row.row_id}
                    </div>
                  )}

                  {/* Image Previews */}
                  {row.primaryImage ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--text3)' }}>Primary Image (image_url):</div>
                      <img 
                        src={row.primaryImage} 
                        alt="Primary" 
                        onError={(e) => {
                          const target = e.currentTarget;
                          const key = row.primaryImage?.split('.com/')[1];
                          if (key && !target.src.includes('/s3-image-proxy')) {
                            target.src = `/s3-image-proxy?key=${encodeURIComponent(key)}`;
                          }
                        }}
                        style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)' }} 
                      />

                      {row.secondaryImages && row.secondaryImages.length > 0 && (
                        <>
                          <div style={{ fontSize: '10px', color: 'var(--text3)', marginTop: '4px' }}>
                            Secondary Images ({row.secondaryImages.length}):
                          </div>
                          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto' }}>
                            {row.secondaryImages.map((sec, idx) => (
                              <img 
                                key={idx} 
                                src={sec} 
                                alt={`Sec ${idx}`} 
                                onError={(e) => {
                                  const target = e.currentTarget;
                                  const key = sec?.split('.com/')[1];
                                  if (key && !target.src.includes('/s3-image-proxy')) {
                                    target.src = `/s3-image-proxy?key=${encodeURIComponent(key)}`;
                                  }
                                }}
                                style={{ width: '54px', height: '54px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border)' }} 
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ height: '140px', border: '1px dashed var(--border2)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: 'var(--text3)', textAlign: 'center', padding: '10px' }}>
                      Type Row ID or upload CSV — images auto-fetch dynamically from S3
                    </div>
                  )}
                </div>

                {/* Right Column: Product Metadata Form */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="section-label">2. Product Details</div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Product Name *</label>
                      <input 
                        type="text" 
                        className="pid-input" 
                        placeholder="e.g. Cotton Side Dori Short Kurti"
                        value={row.name}
                        onChange={e => updateRow(row.id, { name: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Brand</label>
                      <input 
                        type="text" 
                        className="pid-input" 
                        placeholder="Slayd Original"
                        value={row.brand}
                        onChange={e => updateRow(row.id, { brand: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Price (₹) *</label>
                      <input 
                        type="number" 
                        className="pid-input" 
                        placeholder="999"
                        value={row.price}
                        onChange={e => updateRow(row.id, { price: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Discount Price (₹)</label>
                      <input 
                        type="number" 
                        className="pid-input" 
                        placeholder="390"
                        value={row.discount_price}
                        onChange={e => updateRow(row.id, { discount_price: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Color</label>
                      <input 
                        type="text" 
                        className="pid-input" 
                        placeholder="e.g. Blush Pink"
                        value={row.color}
                        onChange={e => updateRow(row.id, { color: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Available Sizes</label>
                      <input 
                        type="text" 
                        className="pid-input" 
                        placeholder="XS, S, M, L, XL"
                        value={row.available_sizes}
                        onChange={e => updateRow(row.id, { available_sizes: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Gender</label>
                      <input 
                        type="text" 
                        className="pid-input" 
                        placeholder="Women"
                        value={row.gender}
                        onChange={e => updateRow(row.id, { gender: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--accent)', display: 'block', marginBottom: '4px' }}>Related Product IDs</label>
                      <input 
                        type="text" 
                        className="pid-input" 
                        placeholder="e.g. 4038, 4039"
                        value={row.related_products}
                        onChange={e => updateRow(row.id, { related_products: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  {/* Related Product Tags Display */}
                  {relatedArray.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '10px', color: 'var(--text3)' }}>Linked Related Products:</span>
                      {relatedArray.map((relId, idx) => (
                        <span key={idx} style={{ fontSize: '10px', background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--accent)', padding: '2px 8px', borderRadius: '12px' }}>
                          ID #{relId}
                        </span>
                      ))}
                    </div>
                  )}

                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text2)', display: 'block', marginBottom: '4px' }}>Description</label>
                    <textarea 
                      className="pid-input" 
                      placeholder="Product description and style details..."
                      value={row.description}
                      onChange={e => updateRow(row.id, { description: e.target.value })}
                      style={{ width: '100%', minHeight: '54px', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                    <button 
                      className="run-btn primary" 
                      style={{ width: 'auto', padding: '8px 24px' }}
                      onClick={() => handleSubmitRow(row)}
                      disabled={row.dbStatus === 'submitting'}
                    >
                      {row.dbStatus === 'submitting' ? 'Submitting...' : 'Submit to Database'}
                    </button>
                  </div>

                  {row.error && <div className="row-error">{row.error}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
